import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { Button } from "./ui/button";
import { BsArrowReturnLeft, BsStar } from "react-icons/bs";
import { FaRegCopy } from "react-icons/fa";
import { TbFileExport } from "react-icons/tb";
import { FaRegFilePdf } from "react-icons/fa";
import { useCreateRequest } from "../../hooks/useCreateRequest";
import { ChatMessage, ISubmitFeedbackRequest } from "../../types/discuss.types";
import { ResearchChatMessage } from "../../types/research.types";
import { useSubmitFeedback } from "../../hooks/useSubmitFeedback";
import { toast } from "sonner";
import { useMessageActions } from "../hooks/useMessageActions";
import CreateRequestDialog from "./request-gallery/CreateRequestDialog";

const RatingDialog = React.lazy(() => import("./RatingDialog"));

type Message = ChatMessage | ResearchChatMessage;

export interface MessageActionsProps {
  messageText: string;
  showRating?: boolean;
  showSaveToBank?: boolean;
  showSaveToLibrary?: boolean;
  showCreateDocument?: boolean;
  message: Message;
  prevMessage?: Message;
  documentId?: string;
  documentTitle?: string;
  source: "discuss" | "research";
}

interface ActionItemProps {
  icon: React.ReactNode;
  tooltipContent: string;
  onClick: () => void;
  disabled?: boolean;
}

const ActionItem: React.FC<ActionItemProps> = ({
  icon,
  tooltipContent,
  onClick,
  disabled = false,
}) => {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Button
            className="p-0 size-auto"
            variant="ghost"
            size="icon"
            onClick={onClick}
            disabled={disabled}
          >
            {icon}
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent>{tooltipContent}</TooltipContent>
    </Tooltip>
  );
};

const MessageActions: React.FC<MessageActionsProps> = ({
  messageText,
  showRating = true,

  showSaveToLibrary = true,
  showCreateDocument = true,
  message,
  prevMessage,
  documentId,
  documentTitle,
  source,
}) => {
  const [showRatingDialog, setShowRatingDialog] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useCreateRequest();
  const { mutateAsync: submitFeedback } = useSubmitFeedback();

  const [isCreateRequestOpen, setIsCreateRequestOpen] = React.useState(false);
  const [createRequestData, setCreateRequestData] = React.useState<{ name: string; content: string } | undefined>(undefined);

  const {
    handleCopyMessage,
    handleInsertMessageToDocument,
    handleCreateDocumentFromMessage,


  } = useMessageActions({
    documentId,
    documentTitle,
  });

  const handleRatingClick = () => {
    setShowRatingDialog(true);
  };

  const handleRatingSubmit = async (rating: number, comment: string) => {
    if (!documentId || !documentTitle) {
      toast.error("Submission Failed", {
        description: "Document context is missing.",
      });
      return;
    }

    setIsSubmitting(true);
    console.log(prevMessage);
    try {
      const feedbackPayload = {
        document_id: [documentId],
        source: (source === "discuss"
          ? "Discuss"
          : "ResearchDraft") as ISubmitFeedbackRequest["source"],
        prompt: null,
        query: message.question,
        response: message.response,
        rating: rating as 1 | 2 | 3 | 4 | 5,
        comment: comment,
        current_document_id: documentId,
        document_title: [documentTitle],
      };

      const response = await submitFeedback(feedbackPayload);

      if (source === "discuss" && rating <= 2 && response?.refinedResponse) {
      } else {
        toast.success("Feedback Submitted", {
          description: "Thank you for your feedback",
        });
      }
      setShowRatingDialog(false);
    } catch (error) {
      // console.error("Failed to submit feedback:", error);
      toast.error("Submission Failed", {
        description: "Failed to submit feedback. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveToLibrary = () => {
    setCreateRequestData({
      name: "", // Name will be generated in the dialog
      content: messageText,
    });
    setIsCreateRequestOpen(true);
  };

  return (
    <TooltipProvider>
      <div className="flex flex-col items-center gap-3">
        {showCreateDocument && (
          <ActionItem
            icon={<FaRegFilePdf className="size-[14px]" />}
            tooltipContent="Create document from message"
            onClick={() => {
              const title = source === "discuss" ? "MS Word Discussion" : "MS Word Research";
              handleCreateDocumentFromMessage(messageText, title);
            }}
          />
        )}

        <ActionItem
          icon={<FaRegCopy className="size-[14px]" />}
          tooltipContent="Copy text"
          onClick={() => handleCopyMessage(messageText)}
        />

        <ActionItem
          icon={<BsArrowReturnLeft className="size-[14px]" />}
          tooltipContent="Insert text"
          onClick={() => handleInsertMessageToDocument(messageText)}
        />

        {showRating && (
          <ActionItem
            icon={<BsStar className="size-[14px]" />}
            tooltipContent="Rate response"
            onClick={handleRatingClick}
          />
        )}

        {showSaveToLibrary && (
          <ActionItem
            icon={<TbFileExport className="size-[14px]" />}
            tooltipContent="Save to request library"
            onClick={handleSaveToLibrary}
          />
        )}


      </div>

      {showRatingDialog && (
        <React.Suspense fallback={null}>
          <RatingDialog
            isOpen={showRatingDialog}
            onOpenChange={setShowRatingDialog}
            onSubmit={handleRatingSubmit}
            isSubmitting={isSubmitting}
          />
        </React.Suspense>
      )}
      <CreateRequestDialog
        open={isCreateRequestOpen}
        onOpenChange={setIsCreateRequestOpen}
        initialData={createRequestData}
        type={source}
      />
    </TooltipProvider>
  );
};

export default MessageActions;
